/**
 * The slot registry: one entry per `SlotKind`, so adding a picker is a new component plus one
 * line here and never a branch in the builder (CLAUDE.md rule 6).
 *
 * `SlotChip` is the placeholder itself. Unconfigured it is grey; configured it takes the text
 * colour and keeps the underline — monday's own two states (`ui-reference/automations/
 * INDEX.md`, "Sentence anatomy"). A slot with a visible default still renders as unconfigured.
 */
import { useRef, useState, type ReactNode } from 'react';
import { ANY_VALUE, type SlotKind } from '@elizmonday/shared';
import { SlotPopover } from './SlotPopover';
import {
  ChoicePicker,
  ColumnPicker,
  DurationPicker,
  GroupPicker,
  ItemValuesEditor,
  LabelPicker,
  LabelSetPicker,
  PersonPicker,
  ReceiverPicker,
  TextSlotEditor,
  CHOICES,
  labelsOfColumn,
  type SlotEditorProps,
} from './pickers';
import { DateTriggerPicker, RecurrencePicker, dateTriggerLabel, recurrenceSummary } from './schedule-pickers';
import {
  APP_NAMES,
  BoardMappingEditor,
  BoardPicker,
  ConnectionSlot,
  EmailEditor,
  EventMappingEditor,
  ItemMappingEditor,
  SlackChannelPicker,
  SlackUserPicker,
  WebhookUrlEditor,
  summariseMapping,
} from './integration-pickers';
import { MessageEditor } from './MessageEditor';
import { localTimezone } from './time';
import type { PickerDataView } from '../types';

export * from './SlotPopover';
export * from './pickers';
export * from './schedule-pickers';
export * from './MessageEditor';
export * from './integration-pickers';
export * from './time';

interface SlotEntry {
  /** The popover's aria label and its width in px, both from the captures. */
  readonly label: string;
  readonly width: number;
  render(props: SlotEditorProps): ReactNode;
}

export const SLOT_REGISTRY: Readonly<Record<SlotKind, SlotEntry>> = {
  column: { label: 'Select a column', width: 268, render: (props) => <ColumnPicker {...props} /> },
  status_label: { label: 'Select value', width: 268, render: (props) => <LabelPicker {...props} /> },
  dropdown_labels: { label: 'Select labels', width: 268, render: (props) => <LabelSetPicker {...props} /> },
  dropdown_condition: { label: 'Select', width: 220, render: (props) => <ChoicePicker {...props} kind="dropdown_condition" /> },
  is_condition: { label: 'Select', width: 180, render: (props) => <ChoicePicker {...props} kind="is_condition" /> },
  group: { label: 'Select a group', width: 268, render: (props) => <GroupPicker {...props} /> },
  receiver: { label: 'Who to notify?', width: 268, render: (props) => <ReceiverPicker {...props} /> },
  person: { label: 'Select a person', width: 268, render: (props) => <PersonPicker {...props} /> },
  recurrence: { label: 'Recurrence', width: 352, render: (props) => <RecurrencePicker {...props} /> },
  date_trigger: { label: 'When to notify on this date?', width: 540, render: (props) => <DateTriggerPicker {...props} /> },
  duration: { label: 'How long?', width: 240, render: (props) => <DurationPicker {...props} /> },
  message: { label: 'Message', width: 452, render: (props) => <MessageEditor {...props} /> },
  text: { label: 'Value', width: 268, render: (props) => <TextSlotEditor {...props} /> },
  value: { label: 'Value', width: 268, render: (props) => <TextSlotEditor {...props} /> },
  assign_behavior: { label: 'Select', width: 200, render: (props) => <ChoicePicker {...props} kind="assign_behavior" /> },
  item_values: { label: 'The new item', width: 300, render: (props) => <ItemValuesEditor {...props} /> },
  // M6-A4 (`specs/M6-A4-integrations.md` §7)
  board: { label: 'Select a board', width: 268, render: (props) => <BoardPicker {...props} /> },
  item_mapping: { label: 'The new item', width: 360, render: (props) => <ItemMappingEditor {...props} /> },
  event_mapping: { label: 'The calendar event', width: 360, render: (props) => <EventMappingEditor {...props} /> },
  board_mapping: { label: 'The new board', width: 320, render: (props) => <BoardMappingEditor {...props} /> },
  slack_channel: { label: 'Slack channel', width: 300, render: (props) => <SlackChannelPicker {...props} /> },
  slack_user: { label: 'Slack user', width: 300, render: (props) => <SlackUserPicker {...props} /> },
  connection: { label: 'Connection', width: 300, render: (props) => <ConnectionSlot {...props} /> },
  email: { label: 'Email', width: 380, render: (props) => <EmailEditor {...props} /> },
  webhook_url: { label: 'Where to POST', width: 380, render: (props) => <WebhookUrlEditor {...props} /> },
};

const columnIdOf = (value: unknown): string | null => {
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null) {
    const id = (value as { columnId?: unknown }).columnId;
    return typeof id === 'string' ? id : null;
  }
  return null;
};

/**
 * What a **configured** slot reads as in the sentence. `null` means "keep monday's grey
 * default label", which is what an unfilled slot and a message slot both do (a configured
 * message still renders as the word "notify" — capture `41-created.png`).
 */
export function formatSlotValue(
  kind: SlotKind,
  value: unknown,
  pickers: PickerDataView,
  context: { columnId: string | null } = { columnId: null },
): string | null {
  const columns = [...pickers.columns, ...pickers.subitemColumns];
  switch (kind) {
    case 'column': {
      const id = columnIdOf(value);
      const column = columns.find((entry) => entry.id === id);
      return column === undefined ? id : column.isSubitemColumn ? `subitem ${column.title}` : column.title;
    }
    case 'status_label': {
      if (typeof value === 'object' && value !== null && (value as Record<string, unknown>)[ANY_VALUE] === true) return 'Anything';
      const index = typeof value === 'object' && value !== null ? Number((value as { index?: number }).index) : Number(value);
      const label = labelsOfColumn(pickers, context.columnId).find((entry) => entry.index === index);
      return label?.text ?? (Number.isFinite(index) ? String(index) : null);
    }
    case 'dropdown_labels': {
      if (!Array.isArray(value) || value.length === 0) return null;
      const labels = labelsOfColumn(pickers, context.columnId);
      return value
        .map((entry) => {
          const index = typeof entry === 'object' && entry !== null ? Number((entry as { index?: number }).index) : Number(entry);
          return labels.find((label) => label.index === index)?.text ?? String(index);
        })
        .join(', ');
    }
    case 'is_condition':
    case 'dropdown_condition':
    case 'assign_behavior': {
      const raw = typeof value === 'object' && value !== null ? (value as { value?: unknown }).value : value;
      return CHOICES[kind]?.find((option) => option.value === raw)?.text ?? null;
    }
    case 'group': {
      if (value === 'top') return `Top group (currently '${pickers.groups[0]?.title ?? ''}')`;
      return pickers.groups.find((group) => group.id === value)?.title ?? (typeof value === 'string' ? value : null);
    }
    case 'person': {
      const ids = Array.isArray(value) ? value.map(String) : [String(value)];
      const names = ids.map((id) => pickers.people.find((person) => person.id === id)?.name ?? id);
      return names.length === 0 ? null : names.join(', ');
    }
    case 'receiver': {
      const receiver = (value ?? {}) as { type?: string; value?: unknown };
      if (receiver.type === 'me') return pickers.me?.name ?? 'Me';
      if (receiver.type === 'user') return pickers.people.find((person) => person.id === String(receiver.value))?.name ?? 'someone';
      if (receiver.type === 'column') return columns.find((column) => column.id === String(receiver.value))?.title ?? 'someone';
      if (receiver.type === 'subscriber') return receiver.value === 'board' ? 'board subscribers' : 'subscribers';
      if (receiver.type === 'team' || receiver.type === 'board') return 'the team';
      return null;
    }
    case 'recurrence': {
      const config = value as Parameters<typeof recurrenceSummary>[0] | null;
      if (config === null || typeof config !== 'object') return null;
      // The card drops the "at <time>" tail monday keeps only inside the picker preview.
      return recurrenceSummary(config, config.timezone || localTimezone()).replace(/ at .*$/, '');
    }
    case 'date_trigger':
      return dateTriggerLabel(value as { offset?: { count?: number; unit?: string } });
    case 'duration': {
      const entries = Object.entries((value ?? {}) as Record<string, number>);
      const [unit, count] = entries[0] ?? [];
      if (unit === undefined || count === undefined) return null;
      const noun = unit === 'businessDays' ? 'business day' : unit.replace(/s$/, '');
      return `${String(count)} ${noun}${count === 1 ? '' : 's'}`;
    }
    case 'item_values':
      return 'an item';
    // M6-A4
    case 'board': {
      const id = value === null || value === undefined ? null : String(value);
      return id === null ? null : (pickers.boards.find((board) => board.id === id)?.name ?? id);
    }
    case 'item_mapping':
    case 'event_mapping': {
      const rows = summariseMapping(value);
      return rows.length === 0 ? null : rows.length === 1 ? `${rows[0]?.target ?? ''}` : `${String(rows.length)} fields`;
    }
    case 'board_mapping': {
      const name = (value as { boardName?: unknown } | null)?.boardName;
      return typeof name === 'string' && name.length > 0 ? name : null;
    }
    case 'slack_channel':
    case 'slack_user': {
      const target = value as { value?: unknown; title?: unknown } | null;
      if (target === null || typeof target !== 'object') return null;
      const title = typeof target.title === 'string' && target.title.length > 0 ? target.title : String(target.value ?? '');
      return title.length === 0 ? null : kind === 'slack_channel' ? `#${title}` : title;
    }
    case 'connection': {
      const provider = (value as { authProvider?: unknown } | null)?.authProvider;
      return typeof provider === 'string' ? (APP_NAMES[provider.toLowerCase() as keyof typeof APP_NAMES] ?? provider) : null;
    }
    case 'email': {
      const subject = (value as { subject?: unknown } | null)?.subject;
      return typeof subject === 'string' && subject.length > 0 ? subject : null;
    }
    case 'webhook_url': {
      if (typeof value !== 'string' || value.length === 0) return null;
      try {
        return new URL(value).host;
      } catch {
        return value;
      }
    }
    case 'message':
      // Configured or not, the word stays; the styling is what changes.
      return null;
    case 'text':
    case 'value':
      return typeof value === 'string' && value.length > 0 ? value : null;
    default:
      return null;
  }
}

export interface SlotChipProps {
  kind: SlotKind;
  /** monday's grey default word, shown while the slot is unconfigured. */
  label: string;
  value: unknown;
  filled: boolean;
  pickers: PickerDataView;
  columnId: string | null;
  columnTypes?: readonly string[] | undefined;
  onChange(next: unknown): void;
  readOnly?: boolean;
  testId?: string;
  /**
   * **P-200.** monday's column picker ends with **+ Add a new column**, and `ColumnPicker` draws
   * that row only when it is handed a handler. Nothing handed it one, so the row was dead code
   * on every slot of every recipe: a sentence needing a column the board does not have had to be
   * abandoned mid-way. The host (the centre, which owns the board id and the picker reload)
   * creates the column and answers with it; the slot then takes it, in the same gesture, which
   * is what monday does. `null` means the host declined or the create failed — the popover stays
   * open and the slot is unchanged.
   */
  onAddColumn?: ((input: { columnTypes?: readonly string[] | undefined }) => Promise<{ id: string; type: string } | null>) | undefined;
}

export function SlotChip({
  kind,
  label,
  value,
  filled,
  pickers,
  columnId,
  columnTypes,
  onChange,
  readOnly,
  testId,
  onAddColumn,
}: SlotChipProps) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const entry = SLOT_REGISTRY[kind];
  const text = (filled ? formatSlotValue(kind, value, pickers, { columnId }) : null) ?? label;

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className="ez-automation-slot"
        data-filled={filled}
        data-kind={kind}
        data-testid={testId}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={readOnly === true}
        onClick={() => setOpen((current) => !current)}
      >
        {text}
      </button>
      {open ? (
        <SlotPopover
          anchorRef={anchorRef}
          label={entry.label}
          width={entry.width}
          onDismiss={() => setOpen(false)}
          testId={`slot-popover-${kind}`}
        >
          {entry.render({
            value,
            pickers,
            columnId,
            columnTypes,
            ...(onAddColumn === undefined
              ? {}
              : {
                  onAddColumn: () => {
                    void (async () => {
                      const created = await onAddColumn({ columnTypes });
                      if (created === null) return;
                      onChange({ columnId: created.id, columnType: created.type });
                      setOpen(false);
                    })();
                  },
                }),
            close: () => setOpen(false),
            onChange: (next) => {
              onChange(next);
              // The multi-select pickers stay open; the single-choice ones close on pick.
              // The multi-step editors close themselves through `close()`.
              const staysOpen = ['dropdown_labels', 'recurrence', 'date_trigger', 'duration', 'item_mapping', 'event_mapping'];
              if (!staysOpen.includes(kind)) {
                setOpen(false);
              }
            },
          })}
        </SlotPopover>
      ) : null}
    </>
  );
}
